import './style.css';
import { useEffect, useState } from 'react';
import useRolesStore from 'store/roles';
import { t } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { debounce } from 'lodash';
import {
  confirm,
  createModal,
  RichTable,
  Tooltip,
  useNotificationsStore
} from '@thermon-platforms/thermon-ui-core';
import EditModal from './edit-modal';
import { parseErrors } from 'util/api';


function Roles() {
  const { _ } = useLingui();
  const [rolesStore, rolesStoreActions] = useRolesStore();
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [, notificationsStoreActions] = useNotificationsStore();

  useEffect(() => {
    rolesStoreActions.query();
  }, []);

  useEffect(() => {
    // Set initial, unfiltered state
    setFilteredRoles(rolesStore.roles);
  }, [rolesStore]);

  // Update filtered roles based on search
  // NOTE - debounce here to prevent rapid-fire changes from user typing
  const updateSearch = debounce(function (search) {
    setFilteredRoles(
      rolesStore.roles.filter(role =>
        // NOTE - this is a really coarse search by just searching the whole
        // model but there likely won't be many roles and it's fast and easy
        role.name.toLowerCase().includes(search.toLowerCase())
      )
    );
  }, 200);


  function editRole(role) {
    const editModal = createModal(<EditModal role={role} />);
    editModal.open().catch(() => {/* no-op */});
  }

  async function deleteRole(role) {
    const result = await confirm({
      header: `Are you sure you want to remove ${role.name}?`,
    });

    if (result) {
      rolesStoreActions.remove(role)
        .catch(async res => {
          const errors = parseErrors(await res.json());
          // Notify the user
          notificationsStoreActions.notify(
            errors?.roles || t`Failed to delete role`,
            'alert'
          );
        });
    }
  }

  const columns = [
    {name: t`Name`, key: 'name', sortable: true},
    {name: t`Permissions`, key: 'permissions'},
    t`Actions`
  ];

  function RolesTableRow({row, index}) {
    return (
      <tr>
        <td>{row.name}</td>
        <td>
          { row.permissions == '*' ?
            t`All` :
            <>{ row.permissions?.map(item => _(rolesStore.permissions[item])).join(', ') }</>
          }
        </td>
        <td className="actions">
          <button
            className="edit"
            data-tooltip-content={t`Edit`}
            data-tooltip-id={`control-tooltip-row-${index}`}
            onClick={() => editRole(row)}
            disabled={row.name == 'Admin'}
          >
            <Trans>Edit</Trans>
          </button>
          <button
            className="delete danger"
            data-tooltip-content={t`Delete`}
            data-tooltip-id={`control-tooltip-row-${index}`}
            onClick={() => deleteRole(row)}
            disabled={row.name == 'Admin'}
          >
            <Trans>Delete</Trans>
          </button>
          {/*
            NOTE - This shouldn't be necessary since there's already a tooltip
            container in the main view but it doesn't work after navigation.
            This fixes it so ¯\_(ツ)_/¯
          */}
          <Tooltip id={`control-tooltip-row-${index}`} />
        </td>
      </tr>
    )
  }

  return (
    <div className="view roles">
      <header>
        <h2><Trans>Role Managment</Trans></h2>
      </header>
      <section>
        <div className="table-controls">
          <span className="search-input">
            <input
              type="text"
              className="search"
              placeholder={t`Search Roles`}
              onChange={event => updateSearch(event.target.value)}
            />
          </span>
          <button
            className="add-role"
            data-tooltip-content={t`Add Role`}
            data-tooltip-id="control-tooltip"
            onClick={() => editRole({permissions: []})}
          >
            <Trans>Add Role</Trans>
          </button>
        </div>
        <RichTable
          columns={columns}
          rows={filteredRoles}
          loading={rolesStore.loading}
          responsive={true}
          sortable={true}
          Row={RolesTableRow}
          items="roles"
        />
        <Tooltip id="control-tooltip" />
      </section>
    </div>
  );
}

export default Roles;
